// pages/ResetPassword.tsx — "/reset-password?token=..." (M6 Task 5, spec D2). The token
// arrives as a QUERY param, not a route param: better-auth's own `GET
// /api/auth/reset-password/:token` redirect handler 302s the browser here with `?token=...`
// appended to whatever `redirectTo` was passed to `requestPasswordReset` (see
// `RequestReset.tsx` — `redirectTo: '/reset-password'`) — this screen never sees the raw
// path-form token better-auth issued, only the query-string one it forwards along.
import { BrandLogo } from '@repo/ui/components/brand-logo';
import { Button } from '@repo/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/components/card';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { authClient } from '../lib/auth-client';
import { useT } from '../locales/context';

export function ResetPassword() {
  const t = useT();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setError(null);
    setIsSubmitting(true);
    const { error: resetError } = await authClient.resetPassword({ newPassword, token });
    setIsSubmitting(false);
    if (resetError) {
      setError(resetError.message ?? t.auth.resetLinkExpiredFallback);
      return;
    }
    navigate('/sign-in?reset=1');
  }

  if (!token) {
    return (
      <div className="grid min-h-screen place-items-center p-6">
        <div className="flex w-full max-w-sm flex-col items-center gap-6">
          <BrandLogo size={40} />
          <Card className="w-full max-w-sm">
            <CardHeader>
              <CardTitle>{t.auth.resetLinkInvalidTitle}</CardTitle>
              <CardDescription>{t.auth.resetLinkInvalidBody}</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/forgot-password" className="text-sm underline">
                {t.auth.requestNewLinkCta}
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <BrandLogo size={40} />
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>{t.auth.chooseNewPasswordTitle}</CardTitle>
            <CardDescription>{t.auth.chooseNewPasswordSubtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
              <div className="flex flex-col gap-2">
                <Label htmlFor="newPassword">{t.auth.newPasswordLabel}</Label>
                <Input
                  id="newPassword"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t.auth.savingPassword : t.auth.saveNewPasswordCta}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
